/**
 * Email utilities — normalize, validate, hash.
 *
 * The SDK never sends raw email upstream. Anything coming in from a form,
 * an adapter, or `emblue.identify({email})` gets hashed client-side
 * (SHA-256 over the normalized form) and only the hex digest leaves the
 * browser. Backend looks up contacts by the same hash.
 *
 * Normalization is deliberately minimal: lowercase + trim. We don't apply
 * gmail-style dot/+ collapsing because it'd round-trip wrong on every
 * other provider and a different normalization in the backend would
 * silently break matching.
 */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export function normalizeEmail(input: string): string {
	return String(input || '').trim().toLowerCase();
}

export function isValidEmail(input: string): boolean {
	const e = normalizeEmail(input);
	if (e.length < 5 || e.length > 254) return false;
	return EMAIL_RE.test(e);
}

/**
 * SHA-256 hex of the normalized email. Uses crypto.subtle (available in all
 * modern browsers — spec §1.1 target ES2020). Returns lowercase hex.
 */
export async function hashEmail(input: string): Promise<string | null> {
	if (!isValidEmail(input)) return null;
	const normalized = normalizeEmail(input);
	const cryptoObj = (globalThis as any).crypto as Crypto | undefined;
	const subtle = cryptoObj?.subtle;
	if (!subtle || typeof subtle.digest !== 'function') return null;
	const data = new TextEncoder().encode(normalized);
	// Node's webcrypto SubtleCrypto requires `this` to be the subtle object,
	// so we call it as a property access (not as a detached function).
	const buf = await cryptoObj!.subtle.digest('SHA-256', data);
	const bytes = new Uint8Array(buf);
	let hex = '';
	for (let i = 0; i < bytes.length; i++) {
		const v = bytes[i];
		hex += v < 16 ? '0' + v.toString(16) : v.toString(16);
	}
	return hex;
}
